import { z } from 'zod'

import { PublishedProductStatusSchema } from '@/lib/api/schemas/inventory.schema'

const VerdictSchema = z.enum(['consistent', 'points_at_other_piece', 'unproven'])

export const SellerSkuAliasSchema = z.object({
  id: z.string(), productId: z.string(), providerAccountId: z.string(), providerCode: z.string(), accountCode: z.string(), value: z.string(), supersededAt: z.string().nullable(), verdict: z.literal('consistent'),
}).strict()

const AttributionBaseSchema = z.object({
  productId: z.string(), verdict: VerdictSchema, pointsAtProductId: z.string().nullable(),
}).strict()

export const ListingEvidenceSchema = AttributionBaseSchema.extend({
  id: z.string(), providerCode: z.string(), accountCode: z.string(), externalOfferId: z.string(), supersededAt: z.string().nullable(),
}).strict()

const OrderEvidenceSchema = AttributionBaseSchema.extend({
  sourceId: z.string(), externalOrderId: z.string(), position: z.number(), orderStatus: z.string().nullable(), offerId: z.string().nullable(),
}).strict()

export const SalesLinkEvidenceSchema = OrderEvidenceSchema.extend({ bucket: z.string(), quantity: z.number() }).strict()
export const OrderRelationEvidenceSchema = OrderEvidenceSchema.extend({ matchReason: z.string(), matchRule: z.string() }).strict()

export const MediaEvidenceSchema = AttributionBaseSchema.extend({
  id: z.string(), kind: z.string(), position: z.number(), isPrimary: z.boolean(), source: z.string(), sourceUrl: z.string().nullable(), sourceFolder: z.string().nullable(), status: z.string(), mediaAssetId: z.string().nullable(), mediaUrl: z.string().nullable(),
}).strict()

export const PieceEvidenceSchema = z.object({
  productId: z.string(), article: z.string().nullable(), size: z.string().nullable(), publishedStatus: PublishedProductStatusSchema, itemCard: z.string().nullable(), uin: z.string().nullable(), seriesCode: z.string().nullable(), weightGrams: z.string().nullable(), sellerSkuAliases: z.array(SellerSkuAliasSchema), orderRelations: z.array(OrderRelationEvidenceSchema), salesLinks: z.array(SalesLinkEvidenceSchema), listings: z.array(ListingEvidenceSchema), media: z.array(MediaEvidenceSchema),
}).strict()

export const ProductEvidenceSchema = z.object({ productId: z.string(), siblings: z.array(PieceEvidenceSchema), hasConflicts: z.boolean() }).strict()

export const AttributionActionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('reassign_listing'), listingId: z.string(), targetProductId: z.string() }).strict(),
  z.object({ kind: z.literal('reassign_sales_link'), sourceId: z.string(), externalOrderId: z.string(), position: z.number(), targetProductId: z.string() }).strict(),
  z.object({ kind: z.literal('reassign_order_relation'), sourceId: z.string(), externalOrderId: z.string(), position: z.number(), targetProductId: z.string() }).strict(),
  z.object({ kind: z.literal('move_media'), mediaId: z.string(), targetProductId: z.string(), primary: z.enum(['keep_target', 'make_primary']), proof: z.literal('operator_confirmed').optional() }).strict(),
])

export const RefusalSchema = z.object({ code: z.string(), message: z.string() }).strict()
export const AttributionActionResultSchema = z.object({ action: AttributionActionSchema, before: z.record(z.string(), z.unknown()).nullable(), after: z.record(z.string(), z.unknown()).nullable(), proof: z.record(z.string(), z.unknown()), refusal: RefusalSchema.nullable(), previewDigest: z.string() }).strict()
export const AttributionPreviewSchema = z.object({ items: z.array(AttributionActionResultSchema), previewDigest: z.string() }).strict()
export const AttributionApplySchema = AttributionPreviewSchema.extend({ correctionId: z.string() }).strict()

export type ProductEvidence = z.infer<typeof ProductEvidenceSchema>
export type PieceEvidence = z.infer<typeof PieceEvidenceSchema>
export type MediaEvidence = z.infer<typeof MediaEvidenceSchema>
export type AttributionAction = z.infer<typeof AttributionActionSchema>
export type AttributionActionResult = z.infer<typeof AttributionActionResultSchema>
export type AttributionPreview = z.infer<typeof AttributionPreviewSchema>
export type AttributionApply = z.infer<typeof AttributionApplySchema>
